type GridCell = { x: number; y: number };

/** Count from one occupied square to another using the BattleRoom ruler's
 * alternating diagonal rule (D&D Basic Rules, Playing on a Grid).
 * date_of_change: 2026-11-04
 */
export function measureGridDistance(from: GridCell, to: GridCell, gridType: string, feetPerCell: number) {
  if (!Number.isFinite(feetPerCell) || feetPerCell <= 0) throw new Error('A positive grid scale is required.');
  const dx = Math.abs(to.x - from.x);
  const dy = Math.abs(to.y - from.y);
  let cells: number;
  let feet: number;
  if (gridType === 'square') {
    const diagonals = Math.min(dx, dy);
    cells = Math.max(dx, dy);
    feet = (cells + Math.floor(diagonals / 2)) * feetPerCell;
  } else if (gridType === 'hex') {
    const fromRow = from.y - Math.floor((from.x + (from.x & 1)) / 2);
    const toRow = to.y - Math.floor((to.x + (to.x & 1)) / 2);
    cells = Math.max(dx, Math.abs(fromRow - toRow), Math.abs(from.x + fromRow - to.x - toRow));
    feet = cells * feetPerCell;
  } else {
    throw new Error(`Unsupported grid type: ${gridType}`);
  }
  return { cells: Math.ceil(cells), feet: Math.ceil(feet), isDiagonal: gridType === 'square' && dx > 0 && dy > 0, horizontal: dx, vertical: dy };
}

export function gridFootprintCells(origin: GridCell, width: number, height: number): GridCell[] {
  const cells: GridCell[] = [];
  for (let x = 0; x < width; x++) {
    for (let y = 0; y < height; y++) cells.push({ x: origin.x + x, y: origin.y + y });
  }
  return cells;
}

/** A larger creature may attack from any square it occupies. */
export function measureGridFootprintDistance(from: GridCell[], to: GridCell[], gridType: string, feetPerCell: number) {
  let nearest: ReturnType<typeof measureGridDistance> | undefined;
  for (const source of from) {
    for (const target of to) {
      const distance = measureGridDistance(source, target, gridType, feetPerCell);
      if (!nearest || distance.feet < nearest.feet) nearest = distance;
    }
  }
  if (!nearest) throw new Error('Both tokens must occupy a grid cell.');
  return nearest;
}

/** BattleRoom square-grid reach includes diagonal neighbors. Count from the
 * nearest occupied square so preview and authoritative melee agree.
 * Playing on a Grid / simulator reach policy. date_of_change: 2026-11-04
 */
export function measureMeleeFootprintDistance(from: GridCell[], to: GridCell[], feetPerCell: number): number {
  if (!Number.isFinite(feetPerCell) || feetPerCell <= 0) throw new Error('A positive grid scale is required.');
  let cells = Infinity;
  for (const source of from) {
    for (const target of to) cells = Math.min(cells, Math.max(Math.abs(source.x - target.x), Math.abs(source.y - target.y)));
  }
  if (!Number.isFinite(cells)) throw new Error('Both tokens must occupy a grid cell.');
  return cells * feetPerCell;
}
